import {z} from 'zod'
import {compactObj} from '../collection'
import {doTo} from '../promise'
import {queryStringSerializer} from '../queryStringSerializer'
import {zx} from '../type'
import {zAmountType, zQuestionForecastType} from './questionType'
import {omit, pipe} from 'remeda'

export * from './comparisonType'

const keyShape = {
  type: zQuestionForecastType,
  subquestion_id: zx.nullish.string,
  code: zx.nullish.string,
  /** for amounts this is mandatory, but for likelihoods it must be missing */
  amountType: zx.nullishToUndefined(zAmountType).optional()
}

/**
 * A natural key for comparisons, helps us make edits to the question and be able to use the compatible comparisons
 * This is a combination of both likelihood and amount types so that it easily serializes. but we re-declare as a poly type after
 */
const zComparisonStateKey = z.object(keyShape)
export type ComparisonStateKey = z.infer<typeof zComparisonStateKey>
const comparisonKeySerializer = queryStringSerializer(zComparisonStateKey)
/** a unique string for a forecast group */
export const comparisonStateKey = (group: ComparisonStateKey) =>
  comparisonKeySerializer.serialize(
    zComparisonStateKey.strip().parse(
      doTo(group as ComparisonStateKey, ({amountType, ...g}) =>
        compactObj({
          ...g,
          // clean up the default number so we are consistent with our key lookup even if we aren't above
          amountType: amountType == 'number' ? undefined : amountType
        })
      )
    )
  )
export const comparisonStateKeyValue = (key: string) => comparisonKeySerializer.deserialize(key) as ComparisonStateKey

/**
 * A natural key for comparisons, like zComparisonStateKey, but for question sin subq cfg (published) versions. We don't need the subquestion_id here
 */
const zComparisonCfgKey = z.object(omit(keyShape, ['subquestion_id']))
export type ComparisonCfgKey = z.infer<typeof zComparisonCfgKey>
const comparisonCfgKeySerializer = queryStringSerializer(zComparisonCfgKey)
export const comparisonCfgKey = (group: ComparisonCfgKey) => {
  const res = comparisonCfgKeySerializer.serialize(
    zComparisonCfgKey.strip().parse(
      compactObj({
        ...group,
        code: group.code == group.type ? undefined : group.code,
        amountType: group.amountType == 'number' ? undefined : group.amountType
      })
    ) // for the default resolution, don't include the code
  )
  return res
}
export const comparisonCfgKeyValue = (key: string) => comparisonCfgKeySerializer.deserialize(key) as ComparisonCfgKey
